import React, { CSSProperties } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

/**
 * 1 アイテム分の `useSortable` 呼び出しを render-prop で公開する。
 *
 * 表示要素（行・カード・チップ）を呼び出し側が完全に書けるよう、
 * `useSortable` が返す `setNodeRef` / `transform` / `transition` は合成済みの
 * `style` として渡す。ドラッグハンドル用の `attributes` + `listeners` は
 * スプレッドしやすい単一の `handleProps` にまとめる。
 *
 * ハンドルを行全体に付けたい場合は `handleProps` をルート要素にスプレッドし、
 * 一部だけに付けたい場合は `handleProps` を `SortableDragHandle` 等に渡す。
 */
export interface SortableItemRenderArgs {
  /** ルート DOM ノードに渡す ref。 */
  // eslint-disable-next-line no-unused-vars
  setNodeRef: (node: HTMLElement | null) => void;
  /** transform / transition / opacity / 背景 の合成済みスタイル。ルート要素にスプレッドする。 */
  style: CSSProperties;
  /**
   * ドラッグハンドル要素に渡す props（`attributes` + `listeners` の結合）。
   * `<SortableDragHandle {...handleProps} />` のように使う。`@dnd-kit/core` の
   * `DraggableAttributes` と `DraggableSyntheticListeners` を合成した形だが、
   * 用途が「スプレッドして当てるだけ」なので Record で十分。
   */
  handleProps: Record<string, unknown>;
  /** ドラッグ中フラグ（背景色などのフィードバックに使える）。 */
  isDragging: boolean;
}

export interface SortableItemProps {
  /** このアイテムの一意 ID（`SortableList` の `getId(item)` と一致させる）。 */
  id: string;
  /** このアイテム個別の DnD を無効化する（フィルタ表示中など）。 */
  disabled?: boolean;
  /** render-prop。`useSortable` の戻り値から組み立てた引数を受け取る。 */
  // eslint-disable-next-line no-unused-vars
  children: (args: SortableItemRenderArgs) => React.ReactNode;
}

const SortableItem: React.FC<SortableItemProps> = ({
  id,
  disabled = false,
  children,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id, disabled });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    background: isDragging ? "var(--soremo-background)" : undefined,
  };

  const handleProps: Record<string, unknown> = {
    ...attributes,
    ...(listeners ?? {}),
  };

  return <>{children({ setNodeRef, style, handleProps, isDragging })}</>;
};

export default SortableItem;
